import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ErrorBar,
  CartesianGrid,
  Tooltip,
} from "recharts";

/* ---------- CONSTANTS ---------- */

const START_ALL_TIME = new Date(2023, 6, 1); // July 1, 2023
const MIN_SCORE = 200;
const MAX_SCORE = 800;
const MAX_GRAPH = 803;

/* ---------- DATE HELPERS ---------- */

const monthStart = (y, m) => new Date(y, m, 1);

const fmt = (d, opts) => d.toLocaleDateString("en-US", opts);

const generateDates = (range) => {
  const now = new Date();
  const base = monthStart(now.getFullYear(), now.getMonth());
  const dates = [];

  if (range === "6m") {
    for (let i = 6; i >= 0; i--) {
      const d = monthStart(base.getFullYear(), base.getMonth() - i);
      dates.push(fmt(d, { month: "short" }));
    }
  } else if (range === "1y") {
    for (let i = 6; i >= 0; i--) {
      const d = monthStart(base.getFullYear(), base.getMonth() - i * 2);
      dates.push(fmt(d, { month: "short", year: "2-digit" }));
    }
  } else if (range === "all") {
    let d = new Date(START_ALL_TIME);
    while (d <= base) {
      dates.push(fmt(d, { month: "short", year: "2-digit" }));
      d = monthStart(d.getFullYear(), d.getMonth() + 3);
    }
  } else {
    // 3M → half-month cadence, no duplicates
    for (let i = 6; i >= 0; i--) {
      const d = new Date(base);
      d.setDate(1 + i * 15);
      if (d <= now) {
        dates.push(i === 0 ? "Today" : fmt(d, { month: "short", day: "numeric" }));
      }
    }
  }

  return dates;
};

/* ---------- SAMPLE DATA ---------- */

const round10 = (n) => Math.round(n / 10) * 10;

const generateSampleData = (labels) => {
  const start = 520;

  return labels.map((label, i) => {
    const t = i / (labels.length - 1);
    const value = start + (MAX_SCORE - start) * (1 - Math.exp(-3 * t));
    const error = Math.max(25, 80 * (1 - t));

    const avg = Math.min(value, MAX_SCORE);
    const low = Math.max(avg - error, MIN_SCORE);
    const high = Math.min(avg + error, MAX_SCORE);

    return {
      label,
      score: avg,
      error: [avg - low, high - avg],
    };
  });
};

/* ---------- TOOLTIP ---------- */

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const { score, error } = payload[0].payload;
  const [lowDelta, highDelta] = error;

  const estimate = round10(score);
  const low = round10(score - lowDelta);
  const high = round10(score + highDelta);

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.95)",
        border: "1px solid rgba(0,0,0,0.15)",
        borderRadius: 8,
        padding: "10px 12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div>
        Estimate: <strong>{estimate}</strong>
      </div>
      <div>
        Range: <strong>{low}–{high}</strong>
      </div>
    </div>
  );
};

/* ---------- COMPONENT ---------- */

const SectionProgressGraph = ({ range = "6m", history, section }) => {
  const labels = useMemo(() => generateDates(range), [range]);

  const data = useMemo(() => {
    if (!history || !section) return [];

    const entries = Object.values(history)
      .filter((h) => h?.[section]?.est != null)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (!entries.length) return [];

    // 🔑 One bar per real data point (no fake past)
    return entries.map((e) => ({
      label: e.date, // keeps XAxis happy
      score: e[section].est,
      error: [
        e[section].est - e[section].low,
        e[section].high - e[section].est,
      ],
    }));
  }, [history, section]);

  return (
    <Box
      onMouseDown={(e) => e.preventDefault()} // ✅ prevents focus ring on click
      sx={{
        "& .recharts-wrapper, & .recharts-surface": { outline: "none" },
        "& *:focus, & *:focus-visible": { outline: "none", boxShadow: "none" },
      }}
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart
          key={range} // 🔥 FIXES DUPLICATE LABELS
          data={data}
          margin={{ left: 8, right: 16, bottom: 24 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          <XAxis dataKey="label" tick={{ fontSize: 12 }} tickMargin={8} interval="auto" />

          <YAxis domain={[0, MAX_GRAPH]} ticks={[0, 200, 400, 600, 800]} />

          <Tooltip content={<CustomTooltip />} cursor={false} />

          <Bar
            dataKey="score"
            fill="#1976d2"
            fillOpacity={1}
            radius={[6, 6, 0, 0]}
            isAnimationActive={false}
            activeBar={false} // ✅ removes Recharts “active” outline
          >
            <ErrorBar
              dataKey="error"
              direction="y"
              stroke="#000000ff"
              strokeWidth={2}
              strokeOpacity={1}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default SectionProgressGraph;
